/**
 * WartungPage — Seite „Wartung" (Spec §6): beantwortet „auf wen müssen wir
 * zugehen?" in vier Ansichten.
 *
 * - „nach Datum": flache, nach Wartungsende sortierte Liste (wie das Backend
 *   sie liefert — `overview.service.ts::listExpiring`).
 * - „nach Produkt": für die Bereichsbesprechung — ein Wartungsende trifft
 *   selten einen einzelnen Kunden, die Gruppe zeigt Systeme × Kunden auf
 *   einen Blick, Details je Gruppe aufklappbar.
 * - „ohne Zuständigen": Systeme ohne Beauftragung (Sammelstelle gegen
 *   stilles Verrotten, Spec §6) — nicht segment-gescoped im Backend.
 * - „Stand veraltet": Softwarestände, deren Erhebungsdatum die
 *   Veraltungsgrenze überschritten hat — ebenfalls nicht segment-gescoped.
 *
 * Segment- und Zeitraumfilter wirken auf die ersten beiden Ansichten (die
 * einzigen mit `segmentId`/`withinMonths`-Parametern im Backend); die beiden
 * Aufmerksamkeits-Ansichten sind bewusst global (siehe overview.service.ts).
 * Genau deshalb werden die Filter dort auch nicht mehr angezeigt — siehe
 * `VIEW_FILTERS` (Befund U11).
 */
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Select,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { createLink, useSearch } from "@tanstack/react-router";

import { OnlyMineSwitch } from "../components/OnlyMineSwitch.js";
import { parseOnlyMine, useOnlyMine } from "../lib/onlyMine.js";
import { forwardRef, useState, type ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";

import { LifecycleBadge } from "../components/maintenance/LifecycleBadge.js";
import { SystemsBulkTab } from "../components/maintenance/SystemsBulkTab.js";
import { formatIsoDate } from "../lib/date.js";
import {
  useAttention,
  useExpiring,
  type ExpiringRow,
} from "../hooks/useMaintenance.js";
import { useSegments } from "../hooks/useSegments.js";

// ── Search-Param Validation (Deep-Link vom Dashboard-Widget, Muster: visits.tsx) ──

export interface WartungSearch {
  filter?: "red" | "orange";
  onlyMine?: boolean;
}

export function validateWartungSearch(raw: Record<string, unknown>): WartungSearch {
  const filter = raw.filter as string | undefined;
  const onlyMine = parseOnlyMine(raw.onlyMine);
  return {
    filter: filter === "red" || filter === "orange" ? filter : undefined,
    ...(onlyMine !== undefined ? { onlyMine } : {}),
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const WITHIN_MONTHS_OPTIONS = [3, 6, 12, 24, 36];
const ALL_SEGMENTS_VALUE = "__all__";

/**
 * Welcher Filter auf welcher Ansicht tatsächlich WIRKT (Befund U11).
 *
 * Der Zeitraumfilter stand über allen vier Ansichten, auch über „Ohne
 * Zuständigen" — dort filterte er nichts. Ein Bedienelement ohne Wirkung ist
 * schlimmer als keines: es lässt den Anwender glauben, er habe die Liste
 * eingeschränkt.
 *
 * Abgelesen an den Datenquellen, nicht geschätzt:
 * - „nach Datum" und „nach Produktversion" holen `GET /maintenance/expiring`
 *   mit `segmentId` und `withinMonths` — beide Filter wirken.
 * - „Ohne Zuständigen" (`listWithoutOwner`) und „Stand veraltet"
 *   (`listStale`) hängen an `GET /maintenance/attention`; dieser Endpunkt
 *   kennt weder `segmentId` noch `withinMonths` und ist bewusst global
 *   (siehe overview.service.ts) — beide Filter wirken dort nicht.
 * - Der Ampel-Chip filtert `dateItems` hier im Frontend und wirkt deshalb
 *   nur auf „nach Datum" (die Gruppen der Produktansicht werden nicht
 *   gefiltert).
 */
interface ViewFilters {
  segment: boolean;
  withinMonths: boolean;
  status: boolean;
}

const NO_FILTERS: ViewFilters = { segment: false, withinMonths: false, status: false };

const VIEW_FILTERS: Record<string, ViewFilters> = {
  date: { segment: true, withinMonths: true, status: true },
  product: { segment: true, withinMonths: true, status: false },
  unowned: NO_FILTERS,
  stale: NO_FILTERS,
  // „Inbetriebnahme" listet Systeme OHNE Beauftragung — sie haben damit kein
  // Segment, an dem ein Segmentfilter greifen könnte, und kein Wartungsende,
  // an dem ein Zeitraum greifen könnte.
  bulk: NO_FILTERS,
};

/**
 * Ist der Ampel-Filter aktiv, wirkt der Zeitraum nicht mehr (Befund U11,
 * gleiche Regel wie oben — nur dynamisch statt je Reiter fest).
 *
 * Der Grund liegt im Backend: `GET /maintenance/expiring?status=…` schaltet
 * `withinMonths` bewusst ab, weil die Ampel sich selbst begrenzt. Erst dadurch
 * zeigt der Sprung von der Dashboard-Kachel genau die Zeilen, die die Kachel
 * gezählt hat. Ein Zeitraum-Wähler, der in diesem Zustand nichts mehr
 * einschränkt, wäre wieder ein Bedienelement ohne Wirkung.
 */
function activeFiltersFor(tab: string, statusFilter: "red" | "orange" | null): ViewFilters {
  const base = VIEW_FILTERS[tab] ?? NO_FILTERS;
  if (!base.status || statusFilter === null) return base;
  return { ...base, withinMonths: false };
}

// ── Verlinkung in die Kundenakte (Befund U8) ────────────────────────────────
//
// Die Seite soll in die Kundenansprache führen — „sehen, dass es brennt, dann
// auf den Kunden zugehen". Kunde und System waren bisher reiner Text und der
// Weg endete in der Liste. Beide Ziele nutzen dieselbe Detailroute; der
// Systemweg trägt zusätzlich den Reiter und die System-Kennung als
// Suchparameter (siehe `validateCustomerDetailSearch` in customers.tsx).

/**
 * `Anchor component={Link}` verliert die Routen-Generics (Pfad, Params und
 * Search werden zu `never`) — das Repo behalf sich dafür bisher mit
 * `as never`-Zusicherungen. `createLink` ist der dafür vorgesehene Weg: es
 * bindet Mantines Anchor typsicher an den Router, sodass gerade der neue
 * Suchparameter `systemId` geprüft statt weggecastet wird.
 */
const RouterAnchor = createLink(
  forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<"a"> & { size?: string }>(
    function RouterAnchorBase(props, ref) {
      return <Anchor ref={ref} {...props} />;
    },
  ),
);

function CustomerLink({ customerId, name }: { customerId: string; name: string }) {
  return (
    <RouterAnchor to="/customers/$id" params={{ id: customerId }} size="sm">
      {name}
    </RouterAnchor>
  );
}

function SystemLink({
  customerId,
  systemId,
  name,
}: {
  customerId: string;
  systemId: string;
  name: string;
}) {
  return (
    <RouterAnchor
      to="/customers/$id"
      params={{ id: customerId }}
      search={{ tab: "systeme", systemId }}
      size="sm"
    >
      {name}
    </RouterAnchor>
  );
}

// ── Zuständigkeit (Befund U2) ───────────────────────────────────────────────
//
// Die Spalte hieß „Zuständigkeit", zeigte aber den Namen der Beauftragung —
// und der lautet standardmäßig „Kundenname (Bereich)", wiederholte also nur
// die Kundenspalte. Beide Angaben stehen jetzt getrennt: die Beauftragung als
// das, was sie ist, und daneben der Service Lead als der Mensch, den man
// anspricht.

function EngagementCell({ name }: { name: string | null }) {
  const { t } = useTranslation();
  if (name) return <>{name}</>;
  return (
    <Text size="sm" c="dimmed">
      {t("pages.wartung.noEngagement")}
    </Text>
  );
}

function ServiceLeadCell({ name }: { name: string | null }) {
  const { t } = useTranslation();
  if (name) return <>{name}</>;
  return (
    <Text size="sm" c="dimmed">
      {t("pages.wartung.noServiceLead")}
    </Text>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function WartungPage() {
  const { t } = useTranslation();
  const { data: segments } = useSegments();
  const search = useSearch({ strict: false }) as WartungSearch;

  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [withinMonths, setWithinMonths] = useState(12);
  const [activeTab, setActiveTab] = useState<string | null>("date");
  // Befund U12: Mantines Accordion haelt ALLE Panels im DOM (Accordion.Panel
  // rendert seine Kinder immer in ein `Collapse`, es gibt kein keepMounted).
  // Die Produktansicht baute damit jede Gruppentabelle mit auf, und jedes
  // Auf-/Zuklappen renderte sie alle neu — bei einigen Dutzend Gruppen mit je
  // vielen Kunden genau die Sekunden ohne Rueckmeldung. Welche Gruppe offen
  // ist, steht deshalb hier; das Panel baut seine Tabelle erst dann.
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<"red" | "orange" | null>(
    search.filter ?? null,
  );

  const { onlyMine } = useOnlyMine();
  const expiringOpts = { segmentId: segmentId ?? undefined, withinMonths, onlyMine };
  const {
    data: dateData,
    isLoading: dateLoading,
    isError: dateError,
  } = useExpiring({
    ...expiringOpts,
    // Der Ampel-Filter wird serverseitig angewandt, nicht mehr hier: Nur so
    // deckt sich die Liste mit dem Zähler der Dashboard-Kachel (siehe
    // `listExpiring` im Backend). Die Produktansicht daneben bleibt ungefiltert
    // — der Chip hat dort nie gewirkt.
    ...(statusFilter !== null ? { status: statusFilter } : {}),
  });
  const {
    data: productData,
    isLoading: productLoading,
    isError: productError,
  } = useExpiring({ ...expiringOpts, groupBy: "product" });
  const {
    data: attentionData,
    isLoading: attentionLoading,
    isError: attentionError,
  } = useAttention();

  // Kein zweites Filtern im Browser mehr — der Server hat den Ampel-Filter
  // bereits angewandt. Ein Nachfiltern hier würde die Menge erneut auf den
  // Zeitraum einschränken, den der Server für diesen Fall bewusst weglässt.
  const dateItems = dateData?.items ?? [];
  const productGroups = productData?.groups ?? [];
  const withoutOwner = attentionData?.withoutOwner ?? [];
  const staleItems = attentionData?.stale ?? [];
  const staleAfterMonths = attentionData?.staleAfterMonths;

  const activeFilters = activeFiltersFor(activeTab ?? "", statusFilter);

  const segmentOptions = [
    { value: ALL_SEGMENTS_VALUE, label: t("pages.wartung.allSegments") },
    ...(segments ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={2}>{t("pages.wartung.title")}</Title>
          <Text c="dimmed" size="sm">
            {t("pages.wartung.subtitle")}
          </Text>
        </Stack>
        {/* Befund U11: Filter erscheinen nur auf den Ansichten, auf die sie
            wirken (siehe VIEW_FILTERS). Auf den beiden global gemeinten
            Ansichten steht stattdessen der Grund dafür. */}
        <Group gap="sm" align="flex-end">
          {activeFilters.segment && <OnlyMineSwitch />}
          {activeFilters.segment && (
            <Select
              label={t("common.segment")}
              value={segmentId ?? ALL_SEGMENTS_VALUE}
              data={segmentOptions}
              onChange={(v) => setSegmentId(v && v !== ALL_SEGMENTS_VALUE ? v : null)}
              allowDeselect={false}
              style={{ minWidth: 220 }}
            />
          )}
          {activeFilters.withinMonths && (
            <Select
              label={t("pages.wartung.withinMonthsLabel")}
              value={String(withinMonths)}
              data={WITHIN_MONTHS_OPTIONS.map((m) => ({
                value: String(m),
                label: t("pages.wartung.withinMonthsOption", { count: m }),
              }))}
              onChange={(v) => v && setWithinMonths(Number(v))}
              allowDeselect={false}
              style={{ width: 170 }}
            />
          )}
          {!activeFilters.segment && !activeFilters.withinMonths && (
            <Text c="dimmed" size="sm" maw={320}>
              {t("pages.wartung.globalViewHint")}
            </Text>
          )}
        </Group>
      </Group>

      {/* Der Ampel-Filter wirkt nur auf die Liste „nach Datum" (siehe
          `dateItems` unten sowie MaintenanceWidget.tsx, das per Deep-Link
          genau dorthin verlinkt) — auf den übrigen Reitern hätte der Chip
          keine Wirkung und wird deshalb ausgeblendet statt wirkungslos
          angezeigt zu werden. */}
      {statusFilter && activeFilters.status && (
        <Group gap="xs">
          <Badge color={statusFilter} variant="light">
            {t(`pages.wartung.status.${statusFilter}`)}
          </Badge>
          <Button
            variant="subtle"
            size="xs"
            color="gray"
            onClick={() => setStatusFilter(null)}
          >
            {t("pages.wartung.filterReset")}
          </Button>
        </Group>
      )}

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="date">{t("pages.wartung.tabs.date")}</Tabs.Tab>
          <Tabs.Tab value="product">{t("pages.wartung.tabs.product")}</Tabs.Tab>
          <Tabs.Tab
            value="unowned"
            rightSection={
              withoutOwner.length > 0 ? (
                <Badge size="xs" color="orange" variant="filled">
                  {withoutOwner.length}
                </Badge>
              ) : undefined
            }
          >
            {t("pages.wartung.tabs.unowned")}
          </Tabs.Tab>
          <Tabs.Tab value="stale">{t("pages.wartung.tabs.stale")}</Tabs.Tab>
          <Tabs.Tab value="bulk">{t("pages.wartung.tabs.bulk")}</Tabs.Tab>
        </Tabs.List>

        {/* ── nach Datum ── */}
        <Tabs.Panel value="date" pt="md">
          {dateError ? (
            <Alert color="red" title={t("common.error")}>
              {t("common.errorLoading")}
            </Alert>
          ) : dateLoading ? (
            <Skeleton height={320} />
          ) : (
            <ExpiringTable
              rows={dateItems}
              emptyLabel={t("pages.wartung.emptyDate")}
            />
          )}
        </Tabs.Panel>

        {/* ── nach Produkt ── */}
        <Tabs.Panel value="product" pt="md">
          {productError ? (
            <Alert color="red" title={t("common.error")}>
              {t("common.errorLoading")}
            </Alert>
          ) : productLoading ? (
            <Skeleton height={320} />
          ) : productGroups.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              {t("pages.wartung.emptyProduct")}
            </Text>
          ) : (
            <Accordion
              variant="separated"
              multiple
              value={openGroups}
              onChange={setOpenGroups}
            >
              {productGroups.map((group) => {
                const key = `${group.productId}|${group.versionLabel}`;
                // Alle Zeilen einer Gruppe teilen dieselbe Version und damit
                // dasselbe wirksame Wartungsende und dieselben Produkt-
                // Schwellen (catalog.service.ts::effectiveLifecycle ist pro
                // Version) — die Ampel der ersten Zeile gilt für die ganze
                // Gruppe. `stale` bewusst nicht auf Gruppenebene gezeigt
                // (kann je System variieren) — dafür gibt es den Tab „Stand
                // veraltet".
                const representative = group.rows[0];
                const opened = openGroups.includes(key);
                return (
                  <Accordion.Item key={key} value={key}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="nowrap" pr="sm">
                        <Stack gap={2}>
                          <Text fw={600} size="sm">
                            {group.productName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {group.versionLabel}
                          </Text>
                        </Stack>
                        <Group gap="xs" wrap="nowrap">
                          {representative && (
                            <LifecycleBadge
                              status={representative.status}
                              stale={false}
                              expired={representative.expired}
                              maintenanceEnd={group.maintenanceEnd}
                              granularity={representative.granularity}
                              inheritedFromLine={representative.inheritedFromLine}
                              thresholdsFromProduct={representative.thresholdsFromProduct}
                              warnOrangeMonths={representative.warnOrangeMonths}
                              warnRedMonths={representative.warnRedMonths}
                              staleAfterMonths={representative.staleAfterMonths}
                            />
                          )}
                          <Badge color="gray" variant="light">
                            {t("pages.wartung.systemCount", {
                              count: group.systemCount,
                            })}
                          </Badge>
                          <Badge color="gray" variant="light">
                            {t("pages.wartung.customerCount", {
                              count: group.customerCount,
                            })}
                          </Badge>
                        </Group>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {/* Erst bauen, wenn die Gruppe offen ist — die Daten
                          liegen bereits vollstaendig vor (dieselbe Antwort
                          wie die Kopfzeile), es faellt also kein Nachladen
                          an, nur das Rendern. */}
                      {opened && (
                        <Table striped withTableBorder>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>{t("common.customer")}</Table.Th>
                              <Table.Th>{t("pages.wartung.tableSystem")}</Table.Th>
                              <Table.Th>{t("pages.wartung.tableEngagement")}</Table.Th>
                              <Table.Th>{t("pages.wartung.tableServiceLead")}</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {group.rows.map((row) => (
                              <Table.Tr key={row.systemId}>
                                <Table.Td>
                                  <CustomerLink
                                    customerId={row.customerId}
                                    name={row.customerName}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <SystemLink
                                    customerId={row.customerId}
                                    systemId={row.systemId}
                                    name={row.systemName}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <EngagementCell name={row.engagementName} />
                                </Table.Td>
                                <Table.Td>
                                  <ServiceLeadCell name={row.serviceLeadName} />
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Tabs.Panel>

        {/* ── ohne Zuständigen ── */}
        <Tabs.Panel value="unowned" pt="md">
          {attentionError ? (
            <Alert color="red" title={t("common.error")}>
              {t("common.errorLoading")}
            </Alert>
          ) : attentionLoading ? (
            <Skeleton height={320} />
          ) : (
            <Table highlightOnHover striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("common.customer")}</Table.Th>
                  <Table.Th>{t("pages.wartung.tableSystem")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {withoutOwner.map((row) => (
                  <Table.Tr key={row.systemId}>
                    <Table.Td>
                      <CustomerLink customerId={row.customerId} name={row.customerName} />
                    </Table.Td>
                    <Table.Td>
                      <SystemLink
                        customerId={row.customerId}
                        systemId={row.systemId}
                        name={row.systemName}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
                {withoutOwner.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text c="dimmed" ta="center" py="md">
                        {t("pages.wartung.emptyUnowned")}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>

        {/* ── Stand veraltet ── */}
        <Tabs.Panel value="stale" pt="md">
          {attentionError ? (
            <Alert color="red" title={t("common.error")}>
              {t("common.errorLoading")}
            </Alert>
          ) : attentionLoading ? (
            <Skeleton height={320} />
          ) : (
            <Stack gap="xs">
              {/* Befund U10: Die Markierung „Stand veraltet" nannte ihre
                  Schwelle nicht — man sah, DASS etwas veraltet ist, aber
                  nicht ab wann. Der Wert kommt vom Server mit (siehe
                  `staleAfterMonths` in der Antwort von /maintenance/attention)
                  und steht deshalb auch dann da, wenn die Liste leer ist. */}
              {staleAfterMonths !== undefined && (
                <Text c="dimmed" size="sm">
                  {t("pages.wartung.staleHint", { months: staleAfterMonths })}
                </Text>
              )}
              <ExpiringTable
                rows={staleItems}
                emptyLabel={t("pages.wartung.emptyStale")}
                showAsOf
              />
            </Stack>
          )}
        </Tabs.Panel>

        {/* ── Inbetriebnahme (Sammelbearbeitung) ── */}
        <Tabs.Panel value="bulk" pt="md">
          <SystemsBulkTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ── Shared Table (nach Datum / Stand veraltet) ──────────────────────────────

interface ExpiringTableProps {
  rows: ExpiringRow[];
  emptyLabel: string;
  showAsOf?: boolean;
}

function ExpiringTable({ rows, emptyLabel, showAsOf = false }: ExpiringTableProps) {
  const { t } = useTranslation();
  return (
    <Table highlightOnHover striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("common.customer")}</Table.Th>
          <Table.Th>{t("pages.wartung.tableSystem")}</Table.Th>
          <Table.Th>{t("pages.wartung.tableProduct")}</Table.Th>
          <Table.Th>{t("pages.wartung.tableVersion")}</Table.Th>
          <Table.Th>{t("pages.wartung.tableEngagement")}</Table.Th>
          <Table.Th>{t("pages.wartung.tableServiceLead")}</Table.Th>
          {showAsOf && <Table.Th>{t("maintenance.card.asOf")}</Table.Th>}
          <Table.Th>{t("pages.wartung.tableMaintenanceEnd")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={`${row.systemId}|${row.productId}|${row.versionLabel}`}>
            <Table.Td>
              <CustomerLink customerId={row.customerId} name={row.customerName} />
            </Table.Td>
            <Table.Td>
              <SystemLink
                customerId={row.customerId}
                systemId={row.systemId}
                name={row.systemName}
              />
            </Table.Td>
            <Table.Td>{row.productName}</Table.Td>
            <Table.Td>{row.versionLabel}</Table.Td>
            <Table.Td>
              <EngagementCell name={row.engagementName} />
            </Table.Td>
            <Table.Td>
              <ServiceLeadCell name={row.serviceLeadName} />
            </Table.Td>
            {showAsOf && <Table.Td>{formatIsoDate(row.asOf)}</Table.Td>}
            <Table.Td>
              <LifecycleBadge
                status={row.status}
                stale={row.stale}
                expired={row.expired}
                maintenanceEnd={row.maintenanceEnd}
                granularity={row.granularity}
                inheritedFromLine={row.inheritedFromLine}
                thresholdsFromProduct={row.thresholdsFromProduct}
                warnOrangeMonths={row.warnOrangeMonths}
                warnRedMonths={row.warnRedMonths}
                staleAfterMonths={row.staleAfterMonths}
              />
            </Table.Td>
          </Table.Tr>
        ))}
        {rows.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={showAsOf ? 8 : 7}>
              <Text c="dimmed" ta="center" py="md">
                {emptyLabel}
              </Text>
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
}
